import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import io from "socket.io-client";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TollIcon from "@mui/icons-material/Toll";
import Layout from "../components/Layout";
import CurrentLiveTip from "../components/TipOfDay/CurrentLiveTip";
import UpcomingTipDetail from "../components/TipOfDay/UpcomingTipDetail";
import AddTipModal from "../components/TipOfDay/AddTipModal";
import ConfirmationModal from "../components/ConfirmationModal";
import { COLORS, TYPE_META } from "../components/TipOfDay/colors";
import {
  deleteTipOfDay,
  getAllTipsOfDayForAdmin,
  getLiveTipOfDay,
  reorderTipsOfDay,
  getTipOfDayActive,
  setTipOfDayActive,
} from "../service/service";
import { BASE_URL } from "../service/config";

// Backend returns publishDate as an ISO string; the portal displays DD-MM-YYYY everywhere.
const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getUTCFullYear()}`;
};

const monthLabel = (month, year) =>
  new Date(Date.UTC(year, month - 1, 1)).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

// A rolling window centered on whatever month is currently selected, so prev/next arrows
// (which can move well outside any fixed list) always land on a value the Select can show.
const monthOptions = (centerMonth, centerYear, span = 6) => {
  const options = [];
  for (let offset = -span; offset <= span; offset += 1) {
    const date = new Date(Date.UTC(centerYear, centerMonth - 1 + offset, 1));
    options.push({ month: date.getUTCMonth() + 1, year: date.getUTCFullYear() });
  }
  return options;
};

// Spreads the full API shape (audioFileName, per-reaction counts, shownHistory, lastShownDate,
// _id, etc.) so the expandable detail row has everything CurrentLiveTip's equivalent view needs,
// on top of the renamed/derived fields the table row itself already relied on.
const mapTipFromApi = (tip) => ({
  ...tip,
  id: tip._id,
  publishDate: formatDate(tip.publishDate),
  publishDateIso: tip.publishDate,
  comments: tip.commentsCount,
  reactions: tip.reactionsCount,
});

const UpcomingTipRow = ({ tip, onEdit, onDelete, deletingId }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tip.id });
  const [expanded, setExpanded] = useState(false);
  const typeMeta = TYPE_META[tip.type];
  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <>
    <TableRow ref={setNodeRef} style={rowStyle} sx={{ "& > .MuiTableCell-root": { py: 1.5 } }}>
      <TableCell sx={{ color: "text.disabled", cursor: "grab" }} {...attributes} {...listeners}>
        <DragIndicatorIcon fontSize="small" />
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid",
            borderColor: COLORS.border,
            borderRadius: 1,
            fontSize: 14,
            padding: "4px 12px",
          }}
        >
          {tip.order}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ bgcolor: typeMeta.color, width: 28, height: 28, fontSize: 14 }}>
            {tip.type === "audio" ? <VolumeUpIcon sx={{ fontSize: 16 }} /> : "T"}
          </Avatar>
          <Typography variant="body2">{tip.title}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label={typeMeta.label}
          size="small"
          sx={{
            bgcolor: typeMeta.bg,
            color: typeMeta.color,
            fontWeight: 600,
            borderRadius: 1,
            "& .MuiChip-label": { padding: "4px 12px" },
          }}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {tip.reward}
          <TollIcon sx={{ fontSize: 16, color: COLORS.gold }} />
        </Box>
      </TableCell>
      <TableCell>{tip.publishDate}</TableCell>
      <TableCell>{tip.views}</TableCell>
      <TableCell>{tip.rotations}</TableCell>
      <TableCell>{tip.comments}</TableCell>
      <TableCell>{tip.reactions}</TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ color: COLORS.link, borderColor: COLORS.link }}
            onClick={() => onEdit(tip)}
          >
            Edit
          </Button>
          <ConfirmationModal
            title="Delete this tip?"
            description={`"${tip.title}" will be permanently deleted. This can't be undone.`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            loadingLabel="Deleting…"
            loading={deletingId === tip.id}
            destructive
            onConfirm={() => onDelete(tip)}
            renderTrigger={({ openModal, loading }) => (
              <Button
                variant="outlined"
                size="small"
                sx={{ color: COLORS.destructive, borderColor: COLORS.destructive }}
                onClick={openModal}
                disabled={loading}
              >
                {loading ? "Deleting…" : "Delete"}
              </Button>
            )}
          />
          <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={11} sx={{ p: 0, borderBottom: expanded ? undefined : "none" }}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <UpcomingTipDetail tip={tip} />
        </Collapse>
      </TableCell>
    </TableRow>
    </>
  );
};

const TipOfDay = () => {
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  });
  const [typeFilter, setTypeFilter] = useState("all");
  const [tipModal, setTipModal] = useState({ open: false, tip: null });
  const [liveTip, setLiveTip] = useState(null);
  const [liveTipLoading, setLiveTipLoading] = useState(true);
  const [upcomingTips, setUpcomingTips] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [activeSaving, setActiveSaving] = useState(false);

  const fetchLiveTip = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) {
      setLiveTipLoading(true);
    }

    try {
      const result = await getLiveTipOfDay();
      // A failed background refresh must not make a healthy live card disappear. A successful
      // response with no data is different: it means there genuinely is no live tip anymore.
      if (result?.success) {
        setLiveTip(result.data || null);
      }
    } finally {
      if (showLoading) {
        setLiveTipLoading(false);
      }
    }
  }, []);

  // Reactions, views and comment counts only affect the live card. Refresh it in place so the
  // card remains mounted and the unrelated upcoming table never enters its loading state.
  const refreshLiveStats = useCallback(
    () => fetchLiveTip({ showLoading: false }),
    [fetchLiveTip]
  );

  const fetchActiveStatus = useCallback(async () => {
    const result = await getTipOfDayActive();
    if (result?.success) {
      setIsActive(result.data?.enabled !== false);
    }
  }, []);

  const handleToggleActive = async (event) => {
    const nextEnabled = event.target.checked;
    setActiveSaving(true);
    const result = await setTipOfDayActive(nextEnabled);
    setActiveSaving(false);
    if (result?.success) {
      setIsActive(nextEnabled);
      fetchLiveTip();
    }
  };

  const fetchUpcomingTips = useCallback(async () => {
    setUpcomingLoading(true);
    const result = await getAllTipsOfDayForAdmin({ month: viewMonth.month, year: viewMonth.year, type: typeFilter });
    if (result?.success) {
      setUpcomingTips((result.data || []).map(mapTipFromApi));
    }
    setUpcomingLoading(false);
  }, [viewMonth, typeFilter]);

  useEffect(() => {
    fetchLiveTip();
  }, [fetchLiveTip]);

  useEffect(() => {
    fetchUpcomingTips();
  }, [fetchUpcomingTips]);

  useEffect(() => {
    fetchActiveStatus();
  }, [fetchActiveStatus]);

  // Live cross-session updates: the backend broadcasts "tipOfDayLive" whenever which tip is
  // scheduled where changes (create/edit/delete/reorder, and now auto-rotation), so a second
  // browser/coach session sees it without a manual refresh — same convention CurrentLiveTip.js
  // already uses for comments/reactions/views. Held in refs so the socket effect can stay
  // mount-only instead of reconnecting whenever the month/type filter changes.
  const fetchLiveTipRef = useRef(fetchLiveTip);
  fetchLiveTipRef.current = fetchLiveTip;
  const fetchUpcomingTipsRef = useRef(fetchUpcomingTips);
  fetchUpcomingTipsRef.current = fetchUpcomingTips;
  const fetchActiveStatusRef = useRef(fetchActiveStatus);
  fetchActiveStatusRef.current = fetchActiveStatus;

  useEffect(() => {
    const socket = io(BASE_URL, { transports: ["websocket"] });
    socket.on("tipOfDayLive", () => {
      fetchLiveTipRef.current();
      fetchUpcomingTipsRef.current();
      fetchActiveStatusRef.current();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // A new tip's date is no longer picked here — the backend auto-assigns it to the back of the
  // rotation queue on create.
  const handleOpenAddModal = () => {
    setTipModal({ open: true, tip: null });
  };

  const handleDelete = async (tip) => {
    setDeletingId(tip.id);
    const result = await deleteTipOfDay(tip.id);
    setDeletingId(null);
    if (result?.success !== false) {
      await fetchUpcomingTips();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldPosition = upcomingTips.findIndex((tip) => tip.id === active.id);
    const newPosition = upcomingTips.findIndex((tip) => tip.id === over.id);
    const reordered = arrayMove(upcomingTips, oldPosition, newPosition);

    // Reassign the same set of dates (already ascending, since the fetched list is date-sorted)
    // onto the tips in their new order — the move only permutes who has which date.
    const datesAscending = upcomingTips.map((tip) => tip.publishDateIso);
    const payload = reordered.map((tip, index) => ({
      _id: tip.id,
      order: index + 1,
      publishDate: datesAscending[index],
    }));

    setUpcomingTips(
      reordered.map((tip, index) => ({
        ...tip,
        order: index + 1,
        publishDate: formatDate(datesAscending[index]),
        publishDateIso: datesAscending[index],
      }))
    );

    const result = await reorderTipsOfDay(payload);
    if (result?.success === false) {
      fetchUpcomingTips();
    }
  };

  return (
    <Layout>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Tip of the Day
            </Typography>
            <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Manage daily tips, comments, and rewards.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch checked={isActive} onChange={handleToggleActive} disabled={activeSaving} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {isActive ? "Tip of the Day is on" : "Tip of the Day is off"}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
          >
            Add New Tip
          </Button>
        </Box>
      </Box>

      {!isActive && (
        <Typography variant="body2" sx={{ color: COLORS.destructive, mb: 2 }}>
          Tip of the Day is turned off — nothing shows in the app right now, and rotation is paused. Tips can still be managed below.
        </Typography>
      )}

      <AddTipModal
        open={tipModal.open}
        tip={tipModal.tip}
        onClose={() => setTipModal({ open: false, tip: null })}
        onSaved={() => {
          fetchLiveTip();
          fetchUpcomingTips();
        }}
      />

      {!liveTipLoading && liveTip && (
        <CurrentLiveTip
          key={liveTip._id}
          tip={liveTip}
          onStructureChanged={() => {
            fetchLiveTip();
            fetchUpcomingTips();
          }}
          onStatsChanged={refreshLiveStats}
        />
      )}
      {!liveTipLoading && !liveTip && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primaryText, mb: 1.5 }}>
            Current Tip (Live)
          </Typography>
          <Paper sx={{ borderRadius: 2, p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No tip is live today.
            </Typography>
          </Paper>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primaryText }}>
          Upcoming Tips — {monthLabel(viewMonth.month, viewMonth.year)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Select
            size="small"
            value={`${viewMonth.year}-${viewMonth.month}`}
            onChange={(event) => {
              const [year, month] = event.target.value.split("-").map(Number);
              setViewMonth({ month, year });
            }}
          >
            {monthOptions(viewMonth.month, viewMonth.year).map(({ month, year }) => (
              <MenuItem key={`${year}-${month}`} value={`${year}-${month}`}>
                {monthLabel(month, year)}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="audio">Audio</MenuItem>
            <MenuItem value="text">Text</MenuItem>
          </Select>
          <IconButton
            size="small"
            sx={{ border: "1px solid", borderColor: COLORS.border }}
            onClick={() =>
              setViewMonth(({ month, year }) => {
                const date = new Date(Date.UTC(year, month - 2, 1));
                return { month: date.getUTCMonth() + 1, year: date.getUTCFullYear() };
              })
            }
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            sx={{ border: "1px solid", borderColor: COLORS.border }}
            onClick={() =>
              setViewMonth(({ month, year }) => {
                const date = new Date(Date.UTC(year, month, 1));
                return { month: date.getUTCMonth() + 1, year: date.getUTCFullYear() };
              })
            }
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Reward</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Publishes</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Views</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rotations</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Comments</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Reactions</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {upcomingLoading && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}
            {!upcomingLoading && upcomingTips.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ color: "text.secondary", py: 3 }}>
                  No tips scheduled for this month.
                </TableCell>
              </TableRow>
            )}
            {!upcomingLoading && upcomingTips.length > 0 && (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={upcomingTips.map((tip) => tip.id)} strategy={verticalListSortingStrategy}>
                  {upcomingTips.map((tip) => (
                    <UpcomingTipRow
                      key={tip.id}
                      tip={tip}
                      deletingId={deletingId}
                      onEdit={(selected) => setTipModal({ open: true, tip: selected })}
                      onDelete={handleDelete}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Layout>
  );
};

export default TipOfDay;
